import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Advertisement, AdvertisingPrices } from '../../models/types';
import { toast } from 'react-toastify';
import { DollarSign, Check, X, Eye, Calendar, Clock, Settings, Play, Pause } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CarouselSettings {
  speed: number;
  maxAds: number;
  autoPlay: boolean;
}

const AdminAdvertising = () => {
  const { userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [prices, setPrices] = useState<AdvertisingPrices>({
    oneWeek: 50,
    twoWeeks: 90,
    threeWeeks: 120
  });
  const [editingPrices, setEditingPrices] = useState<AdvertisingPrices | null>(null);
  const [carouselSettings, setCarouselSettings] = useState<CarouselSettings>({
    speed: 5000,
    maxAds: 5,
    autoPlay: true
  });
  const [editingCarousel, setEditingCarousel] = useState<CarouselSettings | null>(null);

  useEffect(() => {
    if (userData?.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch advertisements
        const adsSnapshot = await getDocs(collection(db, 'advertisements'));
        const adsList = adsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
          startDate: doc.data().startDate?.toDate(),
          endDate: doc.data().endDate?.toDate()
        })) as Advertisement[];
        
        setAdvertisements(adsList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));

        // Fetch advertising prices
        const pricesDoc = await getDocs(collection(db, 'systemParams'));
        const pricesData = pricesDoc.docs.find(doc => doc.id === 'advertisingPrices');
        if (pricesData) {
          setPrices(pricesData.data() as AdvertisingPrices);
        }

        // Fetch carousel settings
        const carouselDoc = await getDoc(doc(db, 'systemParams', 'carouselSettings'));
        if (carouselDoc.exists()) {
          const data = carouselDoc.data();
          setCarouselSettings({
            speed: data.speed || 5000,
            maxAds: data.maxAds || 5,
            autoPlay: data.autoPlay !== false
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userData, navigate, t]);

  const handleApproveAd = async (adId: string) => {
    try {
      const ad = advertisements.find(a => a.id === adId);
      if (!ad) return;

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(startDate.getDate() + (ad.duration * 7));

      await updateDoc(doc(db, 'advertisements', adId), {
        status: 'active',
        startDate,
        endDate,
        updatedAt: new Date()
      });

      setAdvertisements(prev => prev.map(a => 
        a.id === adId 
          ? { ...a, status: 'active', startDate, endDate, updatedAt: new Date() }
          : a
      ));

      toast.success(t('advertising.approvedSuccessfully'));
    } catch (error) {
      console.error('Error approving advertisement:', error);
      toast.error(t('error.updating'));
    }
  };

  const handleRejectAd = async (adId: string) => {
    try {
      await updateDoc(doc(db, 'advertisements', adId), {
        status: 'rejected',
        updatedAt: new Date()
      });

      setAdvertisements(prev => prev.map(a => 
        a.id === adId 
          ? { ...a, status: 'rejected', updatedAt: new Date() }
          : a
      ));

      toast.success(t('advertising.rejectedSuccessfully'));
    } catch (error) {
      console.error('Error rejecting advertisement:', error);
      toast.error(t('error.updating'));
    }
  };

  const handleSavePrices = async () => {
    if (!editingPrices) return;

    try {
      setSaving(true);
      
      await setDoc(doc(db, 'systemParams', 'advertisingPrices'), editingPrices);
      
      setPrices(editingPrices);
      setEditingPrices(null);
      toast.success(t('advertising.pricesUpdated'));
    } catch (error) {
      console.error('Error updating prices:', error);
      toast.error(t('error.updating'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCarouselSettings = async () => {
    if (!editingCarousel) return;

    try {
      setSaving(true);
      
      await setDoc(doc(db, 'systemParams', 'carouselSettings'), editingCarousel);
      
      setCarouselSettings(editingCarousel);
      setEditingCarousel(null);
      toast.success('Carousel settings updated successfully');
    } catch (error) {
      console.error('Error updating carousel settings:', error);
      toast.error(t('error.updating'));
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'active': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'expired': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">{t('advertising.adminTitle')}</h1>

      {/* Carousel Settings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <Settings className="h-5 w-5 mr-2 text-primary-600" />
            Carousel Settings
          </h2>
        </div>

        {editingCarousel ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Speed (seconds)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={editingCarousel.speed / 1000}
                  onChange={(e) => setEditingCarousel(prev => ({
                    ...prev!,
                    speed: parseInt(e.target.value) * 1000
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Max Ads in Carousel</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editingCarousel.maxAds}
                  onChange={(e) => setEditingCarousel(prev => ({
                    ...prev!,
                    maxAds: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Auto Play</label>
                <div className="flex items-center space-x-2 mt-2">
                  <button
                    onClick={() => setEditingCarousel(prev => ({
                      ...prev!,
                      autoPlay: !prev!.autoPlay
                    }))}
                    className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                      editingCarousel.autoPlay 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {editingCarousel.autoPlay ? (
                      <Play className="h-4 w-4 mr-1" />
                    ) : (
                      <Pause className="h-4 w-4 mr-1" />
                    )}
                    {editingCarousel.autoPlay ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSaveCarouselSettings}
                disabled={saving}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? t('admin.saving') : t('common.save')}
              </button>
              <button
                onClick={() => setEditingCarousel(null)}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">{carouselSettings.speed / 1000}s</div>
                <div className="text-sm text-gray-600">Carousel Speed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">{carouselSettings.maxAds}</div>
                <div className="text-sm text-gray-600">Max Ads</div>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  carouselSettings.autoPlay 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {carouselSettings.autoPlay ? (
                    <Play className="h-4 w-4 mr-1" />
                  ) : (
                    <Pause className="h-4 w-4 mr-1" />
                  )}
                  {carouselSettings.autoPlay ? 'Auto Play' : 'Manual'}
                </div>
              </div>
            </div>
            <button
              onClick={() => setEditingCarousel({ ...carouselSettings })}
              className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              Edit Carousel Settings
            </button>
          </div>
        )}
      </div>

      {/* Advertising Prices */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <div className="h-5 w-5 mr-2 text-primary-600 flex items-center justify-center font-bold">€</div>
            {t('advertising.pricingManagement')}
          </h2>
        </div>

        {editingPrices ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">{t('advertising.oneWeek')} (€)</label>
                <input
                  type="number"
                  min="1"
                  value={editingPrices.oneWeek}
                  onChange={(e) => setEditingPrices(prev => ({
                    ...prev!,
                    oneWeek: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{t('advertising.twoWeeks')} (€)</label>
                <input
                  type="number"
                  min="1"
                  value={editingPrices.twoWeeks}
                  onChange={(e) => setEditingPrices(prev => ({
                    ...prev!,
                    twoWeeks: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{t('advertising.threeWeeks')} (€)</label>
                <input
                  type="number"
                  min="1"
                  value={editingPrices.threeWeeks}
                  onChange={(e) => setEditingPrices(prev => ({
                    ...prev!,
                    threeWeeks: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSavePrices}
                disabled={saving}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? t('admin.saving') : t('common.save')}
              </button>
              <button
                onClick={() => setEditingPrices(null)}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">€{prices.oneWeek}</div>
                <div className="text-sm text-gray-600">{t('advertising.oneWeek')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">€{prices.twoWeeks}</div>
                <div className="text-sm text-gray-600">{t('advertising.twoWeeks')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">€{prices.threeWeeks}</div>
                <div className="text-sm text-gray-600">{t('advertising.threeWeeks')}</div>
              </div>
            </div>
            <button
              onClick={() => setEditingPrices({ ...prices })}
              className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              {t('admin.editPrices')}
            </button>
          </div>
        )}
      </div>

      {/* Advertisements List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Eye className="h-5 w-5 mr-2" />
          {t('advertising.allAdvertisements')} ({advertisements.length})
        </h2>

        {advertisements.length === 0 ? (
          <p className="text-center text-gray-500 py-8">{t('advertising.noAdvertisements')}</p>
        ) : (
          <div className="space-y-6">
            {advertisements.map((ad) => (
              <div key={ad.id} className="border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium">{ad.title}</h3>
                    <p className="text-sm text-gray-600">{ad.companyName} - {ad.providerName}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ad.status)}`}>
                    {t(`advertising.status.${ad.status}`)}
                  </span>
                </div>

                <p className="text-gray-700 mb-4">{ad.description}</p>

                {ad.imageUrl && (
                  <div className="mb-4">
                    <img 
                      src={ad.imageUrl} 
                      alt={ad.title}
                      className="max-w-xs max-h-32 object-cover rounded-lg border"
                    />
                  </div>
                )}

                {ad.websiteUrl && (
                  <div className="mb-4">
                    <a 
                      href={ad.websiteUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {ad.websiteUrl}
                    </a>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{ad.duration} {ad.duration === 1 ? t('advertising.week') : t('advertising.weeks')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="h-4 w-4 mr-1 flex items-center justify-center text-xs font-bold">€</div>
                    <span>€{ad.totalPrice}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{t('project.created')}: {ad.createdAt.toLocaleDateString()}</span>
                  </div>
                  {ad.startDate && ad.endDate && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{ad.startDate.toLocaleDateString()} - {ad.endDate.toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {ad.status === 'pending' && (
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => handleRejectAd(ad.id)}
                      className="px-4 py-2 border border-red-500 text-red-500 rounded hover:bg-red-50 transition-colors flex items-center"
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t('advertising.reject')}
                    </button>
                    <button
                      onClick={() => handleApproveAd(ad.id)}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {t('advertising.approve')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAdvertising;